import { Component, EventEmitter, Input, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { ScheduleQuestionAndOptions } from '../models/Exam';
import { Subject } from 'rxjs';
import { QuillEditorComponent } from '../quill-editor/quill-editor.component';
import { QuestionserviceService } from '../services/questionservice.service';
import { LanguageService } from '../services/language.service';
import { CbtserviceService } from '../services/cbtservice.service';

@Component({
  selector: 'app-essay-subquestions',
  templateUrl: './essay-subquestions.component.html',
  styleUrls: ['./essay-subquestions.component.css']
})
export class EssaySubquestionsComponent {
  @Input() question: ScheduleQuestionAndOptions;
  @Input() examid: number;
  @Input() loadingSubject: Subject<boolean>;
  @Input() networkFailureSubject: Subject<boolean>;

  @Output() updatedanswer = new EventEmitter<string>();

  @ViewChildren('editors') editors: QueryList<QuillEditorComponent>;

  answerHtmls: string[] = [];

  private savedHtmls: string[] = [];

  wordCounts: string[] = [];

  lastSavedTime: Date | null = null;

  private destroyed = false;
  private previousQuestion: ScheduleQuestionAndOptions | null = null;
  private saveLoopRunning = false;

  constructor(
    private questionservice: QuestionserviceService,
    private languageService: LanguageService,
    private cbtservice: CbtserviceService,
  ) { }

  get language() {
    return this.languageService.language;
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const newId = this.question?.examQuestion?.schedulequestionid;
    const prevId = this.previousQuestion?.examQuestion?.schedulequestionid;

    if (newId === prevId) return; // same question, nothing to do

    try {
      this.loadingSubject?.next(true);

      // 1. Save current answers before switching away
      if (this.previousQuestion) {
        await this.persistWithRetry(this.previousQuestion);
      }

      // 2. Load answers for the new question
      if (this.question) {
        this.previousQuestion = this.question;
        const count = this.question.examEssaySubQuestions?.length ?? 0;
        this.answerHtmls = new Array(count).fill('');
        this.savedHtmls = new Array(count).fill('');
        this.wordCounts = new Array(count).fill('');

        const combined = this.questionservice.getSubQuestionAnswersFromStorage(
          this.question.examQuestion.schedulequestionid,
        );

        const allowInput = await this.questionservice.allowEssayAnswerInput(this.question);

        this.question.examEssaySubQuestions.forEach((sq, i) => {
          const html = combined?.[sq.essaysubquestionid] ?? '';
          this.answerHtmls[i] = html;
          this.savedHtmls[i] = html;
        });

        // Push values into editors (they may not be rendered yet — insertAnswerIntoEditor handles retry)
        this.question.examEssaySubQuestions.forEach((_, i) => {
          this.insertIntoEditor(i, this.answerHtmls[i], !allowInput);
        });
      } else {
        this.previousQuestion = null;
        this.answerHtmls = [];
        this.savedHtmls = [];
        this.wordCounts = [];
      }
    } finally {
      this.loadingSubject?.next(false);
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.destroyed = true;
    if (this.previousQuestion) {
      await this.persistWithRetry(this.previousQuestion);
    }
  }


  onEditorContentChange([html, plainText]: [string, string], index: number): void {
    this.answerHtmls[index] = html;
    const maxWords = this.question?.examQuestion?.maxwordcount ?? 0;
    const wordCount = this.countWords(plainText);
    this.wordCounts[index] = wordCount > 0
      ? `${wordCount} word${wordCount !== 1 ? 's' : ''}`
      : '';

    // Emit so parent (QuestionComponent) can track answered state
    const anyAnswered = this.answerHtmls.some(a =>
      this.questionservice.essayAnswerIsNotBlank(a),
    );
    this.updatedanswer.emit(anyAnswered ? html : '');
  }

  onEditorCreated(index: number): void {
    if (!this.saveLoopRunning) {
      this.saveLoopRunning = true;
      this.saveLoop();
    }

    const content = this.answerHtmls[index] ?? '';
    const editorArray = this.editors?.toArray();
    if (editorArray?.[index] && content) {
      editorArray[index].setValue(content);
    }
  }
  
  /**
   * Saves all sub-question answers for `targetQuestion` as a single JSON blob.
   * Skips if nothing has changed since last save.
   */
  async saveData(targetQuestion: ScheduleQuestionAndOptions | null): Promise<void> {
    const q = targetQuestion ?? this.previousQuestion;
    if (!q) return;

    const isDirty =
      QuestionserviceService.mathFieldChanged ||
      this.answerHtmls.some((html, i) => html !== this.savedHtmls[i]);

    if (!isDirty) {
      console.log('EssaySubquestions: skipping save — no changes');
      return;
    }

    const allowInput = await this.questionservice.allowEssayAnswerInput(q);
    if (!allowInput) return;

    // Build combined payload: { [essaysubquestionid]: html }
    const combined: Record<number, string> = {};
    q.examEssaySubQuestions.forEach((sq, i) => {
      let html = this.answerHtmls[i] ?? '';
      if (QuestionserviceService.mathFieldChanged) {
        html = this.updateMathFields(html);
      }
      combined[sq.essaysubquestionid] = html;
    });

    if (QuestionserviceService.mathFieldChanged) {
      QuestionserviceService.mathFieldChanged = false;
    }

    const data = this.questionservice.constructAnswerSaveData(
      q.examQuestion.schedulequestionid,
      q.examination.scheduleid,
      [],
      true,
      this.examid,
    );

    await this.questionservice.saveSubQuestionAnswers(combined, data);

    this.savedHtmls = [...this.answerHtmls];
    this.lastSavedTime = new Date();

    // Notify parent about answered state
    const anyAnswered = this.answerHtmls.some(a =>
      this.questionservice.essayAnswerIsNotBlank(a),
    );
    this.updatedanswer.emit(anyAnswered ? this.answerHtmls[0] : '');
  }

  private async persistWithRetry(q: ScheduleQuestionAndOptions): Promise<void> {
    let retryAttempt = 0;
    const baseRetryDelayMs = 1000;
    const maxRetryDelayMs = 10000;
    while (true) {
      try {
        await this.saveData(q);
        break;
      } catch (err) {
        console.error('EssaySubquestions: save failed, retrying…', err);
        retryAttempt++;
        const exponentialDelay = Math.min(
          Math.pow(2, retryAttempt - 1) * baseRetryDelayMs,
          maxRetryDelayMs
        );
        const jitter = Math.floor(Math.random() * 1000);
        await this.cbtservice.sleep(
          Math.min(exponentialDelay + jitter, maxRetryDelayMs)
        );
      }
    }
  }

  private saveLoop(): void {
    this.saveData(null).finally(() => {
      if (!this.destroyed) {
        setTimeout(
          () => this.saveLoop(),
          1000 * this.questionservice.essaySavingIntervalSeconds,
        );
      }
    });
  }

  private insertIntoEditor(index: number, content: string, readOnly: boolean): void {
    const editorArray = this.editors?.toArray();
    if (editorArray?.[index]) {
      editorArray[index].setValue(content);
      editorArray[index].setReadOnly(readOnly);
      return;
    }

    // Capture the question ID at scheduling time so stale retries abort
    const questionId = this.question?.examQuestion?.schedulequestionid;
    let attempts = 0;

    const tryInsert = () => {
      // Abort if component destroyed or question has changed since we scheduled this
      if (this.destroyed) return;
      if (this.question?.examQuestion?.schedulequestionid !== questionId) return;

      const arr = this.editors?.toArray();
      if (arr?.[index]) {
        arr[index].setValue(content);
        arr[index].setReadOnly(readOnly);
      } else if (++attempts < 5) {
        setTimeout(tryInsert, 100);
      }
    };
    tryInsert();
  }

  private countWords(text: string): number {
    if (!text?.trim()) return 0;
    return text.trim().split(/\s+/).length;
  }

  private updateMathFields(html: string): string {
    const span = document.createElement('span');
    span.style.display = 'none';
    span.innerHTML = html;
    span.querySelectorAll('math-field').forEach((field: any) => {
      if (QuestionserviceService.mathFieldValues?.[field.id]) {
        field.innerHTML = QuestionserviceService.mathFieldValues[field.id];
      }
    });
    return span.innerHTML;
  }

  getLastSavedText(): string {
    if (!this.lastSavedTime) return '';
    const seconds = Math.floor(
      (new Date().getTime() - this.lastSavedTime.getTime()) / 1000,
    );
    if (seconds < 10) return 'just now';
    if (seconds < 25) return 'a few seconds ago';
    if (seconds < 60) return 'less than a minute ago';
    if (seconds < 3600) {
      const m = Math.floor(seconds / 60);
      return `${m} minute${m !== 1 ? 's' : ''} ago`;
    }
    const h = Math.floor(seconds / 3600);
    return `${h} hour${h !== 1 ? 's' : ''} ago`;
  }
}
